import { c as _c } from "react/compiler-runtime";
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { bindOnrampCache } from '../operations/cache-lifecycle.js';
import { useFundingFunnel } from '../operations/funnel-react.js';
import { connectionFailureCode } from './connection-error.js';
import { fundingProfilePresentation, fundingProfileQuoteIntent, readFundingProfile } from './funding-profile.js';
export function useFundingProfile(options) {
  const $ = _c(59);
  const {
    client,
    provider
  } = options;
  const cache = useQueryClient();
  const t0 = options.includeLimits ?? true;
  const t1 = options.connection.canConnect ?? false;
  const t2 = options.connection.verification?.customerId ?? null;
  const t3 = options.connection.quoteContext?.context.destination ?? null;
  const t4 = options.connection.quoteContext?.context.asset ?? null;
  let t5;
  if ($[0] !== options.connection.quoteContext?.methods) {
    t5 = options.connection.quoteContext?.methods ?? [];
    $[0] = options.connection.quoteContext?.methods;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  let t6;
  if ($[2] !== client.cacheKey || $[3] !== options.connection.guest || $[4] !== options.currency || $[5] !== options.decimals || $[6] !== options.sessionProofId || $[7] !== t0 || $[8] !== t1 || $[9] !== t2 || $[10] !== t3 || $[11] !== t4 || $[12] !== t5) {
    t6 = ["onramp", client.cacheKey, "profile", options.sessionProofId, options.currency, options.decimals, t0, options.connection.guest, t1, t2, t3, t4, t5];
    $[2] = client.cacheKey;
    $[3] = options.connection.guest;
    $[4] = options.currency;
    $[5] = options.decimals;
    $[6] = options.sessionProofId;
    $[7] = t0;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const queryKey = t6;
  const t7 = !options.connectionPending;
  let t8;
  if ($[14] !== cache || $[15] !== client || $[16] !== options.connection || $[17] !== options.currency || $[18] !== options.decimals || $[19] !== options.includeLimits || $[20] !== options.sessionProofId || $[21] !== provider) {
    t8 = t9 => {
      const {
        signal
      } = t9;
      return readFundingProfile(client, provider, signal, options.connection, options.includeLimits === false ? undefined : fundingProfileQuoteIntent(options.connection, options.currency, options.decimals, profileQuoteIntent(cache.getQueryData(["onramp", client.cacheKey, "profile-quote", options.sessionProofId]), options.currency, options.decimals)), options.includeLimits ?? true, options.currency);
    };
    $[14] = cache;
    $[15] = client;
    $[16] = options.connection;
    $[17] = options.currency;
    $[18] = options.decimals;
    $[19] = options.includeLimits;
    $[20] = options.sessionProofId;
    $[21] = provider;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      persist: false
    };
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== queryKey || $[25] !== t7 || $[26] !== t8) {
    t10 = {
      queryKey,
      enabled: t7,
      queryFn: t8,
      retry: false,
      staleTime: 0,
      gcTime: Infinity,
      networkMode: "always",
      meta: t9,
      refetchOnWindowFocus: false
    };
    $[24] = queryKey;
    $[25] = t7;
    $[26] = t8;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  const query = useQuery(t10);
  let t11;
  if ($[28] !== cache || $[29] !== client.cacheKey || $[30] !== options.sessionProofId || $[31] !== query.data?.verification) {
    t11 = () => {
      const verification = query.data?.verification;
      if (!verification) {
        return;
      }
      cache.setQueriesData({
        queryKey: ["onramp", client.cacheKey, "discovery", options.sessionProofId]
      }, previous => previous?.customerVerification?.customerId === verification.customerId ? {
        ...previous,
        customerVerification: verification
      } : previous);
    };
    $[28] = cache;
    $[29] = client.cacheKey;
    $[30] = options.sessionProofId;
    $[31] = query.data?.verification;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  const t12 = query.data?.verification;
  let t13;
  if ($[33] !== cache || $[34] !== client || $[35] !== options.sessionProofId || $[36] !== t12) {
    t13 = [cache, client, options.sessionProofId, t12];
    $[33] = cache;
    $[34] = client;
    $[35] = options.sessionProofId;
    $[36] = t12;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  useEffect(t11, t13);
  let t14;
  let t15;
  if ($[38] !== cache || $[39] !== client) {
    t14 = () => {
      bindOnrampCache(client, cache);
    };
    t15 = [client, cache];
    $[38] = cache;
    $[39] = client;
    $[40] = t14;
    $[41] = t15;
  } else {
    t14 = $[40];
    t15 = $[41];
  }
  useEffect(t14, t15);
  let t16;
  if ($[42] !== query.data?.limitsUnavailable || $[43] !== query.error || $[44] !== query.isError) {
    t16 = connectionFailureCode(query.error) ?? (query.isError || query.data?.limitsUnavailable ? "onramp_refresh_failed" : null);
    $[42] = query.data?.limitsUnavailable;
    $[43] = query.error;
    $[44] = query.isError;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  const errorCode = t16;
  const t17 = query.isFetching ? "loading" : query.isError || query.data?.limitsUnavailable ? "error" : "ready";
  let t18;
  if ($[46] !== errorCode || $[47] !== t17) {
    t18 = {
      stage: "profile",
      outcome: t17,
      errorCode
    };
    $[46] = errorCode;
    $[47] = t17;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  useFundingFunnel(client, t18);
  const t19 = query.isPending;
  const t20 = query.isFetching;
  const t21 = query.isError;
  const t22 = query.data;
  let t23;
  if ($[49] !== query.data?.limits) {
    t23 = query.data?.limits ?? [];
    $[49] = query.data?.limits;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== query.data || $[52] !== t23) {
    t24 = fundingProfilePresentation(query.data, t23);
    $[51] = query.data;
    $[52] = t23;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[54] !== errorCode || $[55] !== options.connectionPending || $[56] !== query || $[57] !== t24) {
    t25 = {
      loading: t19,
      refreshing: t20,
      error: t21,
      errorCode,
      data: t22,
      presentation: t24,
      refresh() {
        if (!options.connectionPending) {
          query.refetch({
            cancelRefetch: false
          });
        }
      }
    };
    $[54] = errorCode;
    $[55] = options.connectionPending;
    $[56] = query;
    $[57] = t24;
    $[58] = t25;
  } else {
    t25 = $[58];
  }
  return t25;
}
function profileQuoteIntent(memory, currency, decimals) {
  return memory && Date.now() - memory.recordedAt < 30 * 60 * 1000 && memory.intent.fiatCurrency === currency && memory.intent.fiatDecimals === decimals ? memory.intent : undefined;
}
/** Remember only a previously quoted intent; the profile obtains a NEW quote for current limits. */
export function useFundingProfileQuote(client, sessionProofId, intent, review) {
  const $ = _c(11);
  const cache = useQueryClient();
  let t0;
  let t1;
  if ($[0] !== cache || $[1] !== client || $[2] !== intent || $[3] !== review || $[4] !== sessionProofId) {
    t0 = () => {
      if (!intent || !review || review.limits.length === 0 || review.state === "indicative" || Date.parse(review.expiresAt) <= Date.now()) {
        return;
      }
      const key = ["onramp", client.cacheKey, "profile-quote", sessionProofId];
      cache.setQueryDefaults(key, {
        gcTime: 1800000,
        meta: {
          persist: false
        }
      });
      cache.setQueryData(key, {
        intent,
        recordedAt: Date.now()
      });
    };
    t1 = [client, cache, intent, review, sessionProofId];
    $[0] = cache;
    $[1] = client;
    $[2] = intent;
    $[3] = review;
    $[4] = sessionProofId;
    $[5] = t0;
    $[6] = t1;
  } else {
    t0 = $[5];
    t1 = $[6];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[7] !== cache || $[8] !== client) {
    t2 = () => retainProfileQuoteCleanup(client, cache);
    t3 = [client, cache];
    $[7] = cache;
    $[8] = client;
    $[9] = t2;
    $[10] = t3;
  } else {
    t2 = $[9];
    t3 = $[10];
  }
  useEffect(t2, t3);
}
const quoteOwners = new WeakMap();
function retainProfileQuoteCleanup(client, cache) {
  let caches = quoteOwners.get(client);
  if (!caches) {
    caches = new WeakSet();
    quoteOwners.set(client, caches);
  }
  if (caches.has(cache)) return;
  caches.add(cache);
  // A quote survives the checkout unmount; its cleanup must survive it too.
  client.onDispose(() => {
    cache.removeQueries({
      queryKey: ['onramp', client.cacheKey, 'profile-quote']
    });
  });
}